/**
 * @fileoverview Hook that resolves contact role options from the ui-admin
 *   `entityContactRole` lookup category, falling back to the static seed list
 *   when no values have been configured yet.
 * @module contacts
 *
 * Responsibilities:
 * - Load active `entityContactRole` lookup values via the ui-admin lookupService
 * - Return a sorted string array of role display names for template-builder UI
 * - Maintain CONTACT_ROLES static seed as fallback when lookup is empty
 *
 * Dependencies:
 * - ui-admin/src/services/lookupService (listLookupValues)
 * - @core-domain/forms/contactFormSchema (CONTACT_ROLES static seed)
 *
 * Architecture:
 * - Reads from localStorage-backed lookupService used by system-web
 * - Falls back to CONTACT_ROLES when no entityContactRole values are stored
 *
 * Notes:
 * - The ui-admin lookupService is async (localStorage, not Dexie) but follows
 *   the same listLookupValues API contract as the tenant-web service
 */

import { useState, useEffect } from 'react';
import { CONTACT_ROLES } from '@firesquirrel/core-domain';
import { listLookupValues } from '../services/lookupService';

/**
 * @hook useContactRoles
 *
 * Purpose:
 * - Provides the list of available contact role strings for template-builder
 *   ContactsModule configuration panels
 *
 * Returns:
 * - string[] of role display names, sorted by sortOrder then name
 *
 * Side Effects:
 * - Calls listLookupValues('entityContactRole') on mount
 *
 * Offline Behavior:
 * - Falls back to CONTACT_ROLES static seed when no lookup values exist
 */
export function useContactRoles(): string[] {
  const [roles, setRoles] = useState<string[]>([...CONTACT_ROLES]);

  useEffect(() => {
    listLookupValues('entityContactRole').then((values) => {
      const names = values
        .filter((v) => v.isActive && !v._deleted)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))
        .map((v) => v.name);
      if (names.length > 0) {
        setRoles(names);
      }
    });
  }, []);

  return roles;
}
